import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { LanguageContext, getTranslation } from '../LanguageContext';
import { FaCalendarAlt, FaImages, FaVideo, FaArrowLeft, FaSpinner, FaTags } from 'react-icons/fa';
import './Activities.css';

const activitiesTranslations = {
  en: {
    title: "Activities",
    subtitle: "Discover our activities and events",
    loading: "Loading activities...",
    noActivities: "No activities available yet",
    images: "images",
    videos: "videos",
    viewDetails: "View Details",
    back: "Back",
    allActivities: "All Activities"
  },
  fr: {
    title: "Activités",
    subtitle: "Découvrez nos activités et événements",
    loading: "Chargement des activités...",
    noActivities: "Aucune activité disponible pour le moment",
    images: "images",
    videos: "vidéos",
    viewDetails: "Voir les détails",
    back: "Retour",
    allActivities: "Toutes les activités"
  },
  ar: {
    title: "الأنشطة",
    subtitle: "اكتشف أنشطتنا وفعالياتنا",
    loading: "جاري تحميل الأنشطة...",
    noActivities: "لا توجد أنشطة متاحة حالياً",
    images: "صور",
    videos: "فيديوهات",
    viewDetails: "عرض التفاصيل",
    back: "العودة",
    allActivities: "جميع الأنشطة"
  }
};

const Activities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useContext(LanguageContext);
  const translate = (key) => getTranslation(activitiesTranslations, language, key);
  const isRTL = language === 'ar';

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const res = await axios.get('http://localhost:4444/activities');
        setActivities(res.data.data || []);
      } catch (error) {
        console.error('Error fetching activities:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const locale = language === 'ar' ? 'ar-SA' : language === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="activities-page">
        <div className="activities-loading">
          <FaSpinner className="spin" />
          <p>{translate('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`activities-page ${isRTL ? 'rtl' : ''}`}>
      <div className="activities-hero">
        <div className="activities-hero-overlay"></div>
        <div className="activities-hero-content">
          <div className="activities-hero-nav">
            <Link to="/" className="activities-back-btn">
              <FaArrowLeft />
              <span>{translate('back')}</span>
            </Link>
          </div>
          <h1>{translate('title')}</h1>
          <p>{translate('subtitle')}</p>
        </div>
      </div>

      <div className="activities-content">
        <div className="activities-section-header">
          <h2>
            <FaTags />
            {translate('allActivities')}
          </h2>
        </div>

        {activities.length === 0 ? (
          <div className="activities-empty">
            <FaImages className="empty-icon" />
            <p>{translate('noActivities')}</p>
          </div>
        ) : (
          <div className="activities-grid">
            {activities.map((activity) => (
              <Link to={`/activities/${activity._id}`} key={activity._id} className="activity-card">
                <div className="activity-card-image">
                  {activity.images && activity.images.length > 0 ? (
                    <img src={activity.images[0]} alt={activity.title} loading="lazy" />
                  ) : (
                    <div className="activity-card-placeholder">
                      <FaImages />
                    </div>
                  )}
                  <div className="activity-card-overlay">
                    <span className="activity-view-btn">{translate('viewDetails')}</span>
                  </div>
                </div>
                <div className="activity-card-body">
                  <h3 className="activity-card-title">{activity.title}</h3>
                  {activity.description && (
                    <p className="activity-card-desc">{activity.description.substring(0, 120)}...</p>
                  )}
                  <div className="activity-card-meta">
                    {activity.date && (
                      <span className="activity-meta-item">
                        <FaCalendarAlt />
                        {formatDate(activity.date)}
                      </span>
                    )}
                    {activity.images && activity.images.length > 0 && (
                      <span className="activity-meta-item">
                        <FaImages />
                        {activity.images.length} {translate('images')}
                      </span>
                    )}
                    {activity.videos && activity.videos.length > 0 && (
                      <span className="activity-meta-item">
                        <FaVideo />
                        {activity.videos.length} {translate('videos')}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Activities;
